import React from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import Slider from 'react-slick'

import {
    sendEnterAnnouncementsSoundState,
    sendEnterLandingSoundState,
    playEnterAnnouncementSound,
    playExitAnnouncementSound,
} from 'state/actions/sounds'

import {
    changeMode,
    changeOpenedAnnouncement,
    archiveAnnouncement,
    deleteAnnouncement,
} from 'state/actions/announcements'

import Back from 'components/Back/Back'

import { KEY_CODES } from 'constants'

import {
    ANNOUNCEMENT_MODE,
    ANNOUNCEMENT_ERROR,
    DISABLING_DURATION,
    NAVIGATION_DIRECTION,
    SLIDE_SPEED,
} from '../../constants'

import { Header } from '../Header/Header'
import { Body } from '../Body/Body'
import { FooterViewControls } from '../Footer/FooterViewControls'
import { Navigation } from '../Navigation/Navigation'
import { AnnouncementEditor } from '../AnnouncementEditor/AnnouncementEditor'
import { ConfirmationScreen } from '../ConfirmationScreen/ConfirmationScreen'

import styles from './AnnouncementOverlay.scss'

export class AnnouncementOverlayComponent extends React.Component {
    static propTypes = {
        announcementMode: PropTypes.oneOf(Object.values(ANNOUNCEMENT_MODE)).isRequired,
        announcementError: PropTypes.oneOf(Object.values(ANNOUNCEMENT_ERROR)),
        hasPermissions: PropTypes.bool.isRequired,
        isAnnouncementsLimitReached: PropTypes.bool.isRequired,
        sendEnterAnnouncementsSoundState: PropTypes.func.isRequired,
        sendEnterLandingSoundState: PropTypes.func.isRequired,
        playEnterAnnouncementSound: PropTypes.func.isRequired,
        playExitAnnouncementSound: PropTypes.func.isRequired,
        changeAnnouncementMode: PropTypes.func.isRequired,
        changeOpenedAnnouncement: PropTypes.func.isRequired,
        archiveAnnouncement: PropTypes.func.isRequired,
        deleteAnnouncement: PropTypes.func.isRequired,

        items: PropTypes.array.isRequired,
        currentSlide: PropTypes.number.isRequired,
        accountId: PropTypes.number.isRequired,
        onClose: PropTypes.func.isRequired,
    }

    state = {
        currentSlide: this.props.currentSlide,
        isDisabledEdit: false,
        isDisabledArchive: false,
        isDisabledDelete: false,
        isDisabledCreate: false,
    }

    componentDidMount() {
        document.addEventListener('keydown', this.handleKeydown)
        this.handleError()

        this.props.sendEnterAnnouncementsSoundState()
        this.props.playEnterAnnouncementSound()
    }

    componentDidUpdate(prevProps) {
        const { currentSlide } = this.props

        if (currentSlide !== prevProps.currentSlide) {
            this.slider.slickGoTo(currentSlide)
            this.handleSliderAfterChange(currentSlide)
        }
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this.handleKeydown)
        clearTimeout(this.timerId)

        this.props.sendEnterLandingSoundState()
        this.props.playExitAnnouncementSound()
    }

    handleError() {
        const { announcementError } = this.props

        clearTimeout(this.timerId)

        const isDisabledEdit = announcementError === ANNOUNCEMENT_ERROR.EDIT
        const isDisabledArchive = announcementError === ANNOUNCEMENT_ERROR.ARCHIVE
        const isDisabledDelete = announcementError === ANNOUNCEMENT_ERROR.DELETE
        const isDisabledCreate = announcementError === ANNOUNCEMENT_ERROR.CREATE

        this.setState({
            isDisabledEdit,
            isDisabledArchive,
            isDisabledDelete,
            isDisabledCreate,
        })

        this.timerId = setTimeout(() => {
            this.setState({
                isDisabledEdit: false,
                isDisabledArchive: false,
                isDisabledDelete: false,
                isDisabledCreate: false,
            })
        }, DISABLING_DURATION)
    }

    isViewScreen() {
        const { announcementMode } = this.props

        const isCreating = announcementMode === ANNOUNCEMENT_MODE.CREATE
        const isEditing = announcementMode === ANNOUNCEMENT_MODE.EDIT

        return !(isCreating || isEditing)
    }

    isConfirmationScreen() {
        const { announcementMode } = this.props

        const isArchiving = announcementMode === ANNOUNCEMENT_MODE.ARCHIVE
        const isDeleting = announcementMode === ANNOUNCEMENT_MODE.DELETE

        return (isArchiving || isDeleting)
    }

    handleSliderAfterChange = (currentSlide) => {
        this.setState({ currentSlide })
    }

    handleKeydown = (e) => {
        e.stopPropagation()

        if (e.keyCode === KEY_CODES.ESCAPE) {
            this.handleClosing()
        }
    }

    handleClosing = () => {
        const { announcementMode, items } = this.props
        const { currentSlide } = this.state

        const currentItem = items[currentSlide]

        if (!currentItem || announcementMode === ANNOUNCEMENT_MODE.VIEW) {
            this.props.onClose()
        } else {
            this.handleChangingMode(ANNOUNCEMENT_MODE.VIEW)
        }
    }

    handleChangingMode = (mode) => {
        const { items, changeOpenedAnnouncement, changeAnnouncementMode } = this.props
        const { currentSlide } = this.state

        const { message_id } = items[currentSlide] || {}

        changeOpenedAnnouncement(message_id)
        changeAnnouncementMode(mode)
    }

    handleNavigation = (e, direction) => {
        e.stopPropagation()

        switch (direction) {
        case NAVIGATION_DIRECTION.LEFT:
            this.slider.slickPrev()
            break
        case NAVIGATION_DIRECTION.RIGHT:
            this.slider.slickNext()
            break
        default:
            break
        }
    }

    handleRemoving = () => {
        const { announcementMode, items, archiveAnnouncement, deleteAnnouncement } = this.props
        const { currentSlide } = this.state

        const currentItem = items[currentSlide]

        if (announcementMode === ANNOUNCEMENT_MODE.ARCHIVE) {
            archiveAnnouncement(currentItem.message_id)
        } else if (announcementMode === ANNOUNCEMENT_MODE.DELETE) {
            deleteAnnouncement(currentItem.message_id)
        }
    }

    renderAnnouncementView() {
        const { items, hasPermissions, isAnnouncementsLimitReached } = this.props

        const {
            currentSlide,
            isDisabledEdit,
            isDisabledArchive,
            isDisabledDelete,
            isDisabledCreate,
        } = this.state

        if (!this.isViewScreen()) {
            return null
        }

        return (
            <div>
                <Slider
                    draggable={false}
                    infinite={false}
                    initialSlide={currentSlide}
                    afterChange={this.handleSliderAfterChange}
                    arrows={false}
                    speed={SLIDE_SPEED}
                    ref={slider => (this.slider = slider)}
                >
                    {items.map((item, index) => (
                        <Body
                            {...item}
                            key={`${item.message_id}_${index}`}
                            accountId={this.props.accountId}
                        />
                    ))}
                </Slider>
                {hasPermissions && (
                    <div className={styles.footer}>
                        <FooterViewControls
                            disabledEdit={isDisabledEdit}
                            disabledArchive={isDisabledArchive}
                            disabledDelete={isDisabledDelete}
                            disabledCreate={isDisabledCreate}
                            isAnnouncementsLimitReached={isAnnouncementsLimitReached}
                            onChangeMode={this.handleChangingMode}
                        />
                    </div>
                )}
            </div>
        )
    }

    renderAnnouncementEditor() {
        const { items, announcementMode } = this.props
        const { currentSlide } = this.state

        const isEditing = announcementMode === ANNOUNCEMENT_MODE.EDIT

        if (this.isViewScreen()) {
            return null
        }

        const currentItem = isEditing ? items[currentSlide] : null

        return (
            <AnnouncementEditor
                item={currentItem}
                announcementMode={announcementMode}
                onChangeMode={this.handleChangingMode}
            />
        )
    }

    renderConfirmationScreen() {
        const { announcementMode } = this.props
        const { isDisabledArchive, isDisabledDelete } = this.state

        if (!this.isConfirmationScreen()) {
            return null
        }

        const isArchiving = announcementMode === ANNOUNCEMENT_MODE.ARCHIVE
        const isDisabled = isArchiving ? isDisabledArchive : isDisabledDelete

        return (
            <div className={styles.confirmationScreen}>
                <ConfirmationScreen
                    announcementMode={announcementMode}
                    isDisabled={isDisabled}
                    onApply={this.handleRemoving}
                    onCancel={() => this.handleChangingMode(ANNOUNCEMENT_MODE.VIEW)}
                />
            </div>
        )
    }

    render() {
        const { items, announcementMode } = this.props
        const { currentSlide } = this.state

        const itemsLength = items.length
        const lastItem = itemsLength - 1

        const withNavigation = (itemsLength > 1 && this.isViewScreen())

        const contentWrapperClassNames = classNames(
            styles.contentWrapper,
            this.isConfirmationScreen() && styles.contentWrapper__blur,
        )

        return (
            <div className={styles.base}>
                <div className={styles.back}>
                    <Back
                        caption={Back.CAPTIONS.close}
                        type={Back.TYPES.close}
                        side={Back.SIDES.right}
                        handleClick={this.handleClosing}
                    />
                </div>
                <div className={contentWrapperClassNames}>
                    <div className={styles.header}>
                        <Header
                            currentItem={currentSlide + 1}
                            itemsLength={itemsLength}
                            announcementMode={announcementMode}
                            withPaginationInfo={withNavigation}
                        />
                    </div>
                    <div className={styles.main}>
                        {this.renderAnnouncementView()}
                        {this.renderAnnouncementEditor()}
                    </div>
                    {withNavigation && (
                        <Navigation
                            disabledLeft={currentSlide === 0}
                            disabledRight={currentSlide === lastItem}
                            onClick={this.handleNavigation}
                        />
                    )}
                </div>
                {this.renderConfirmationScreen()}
            </div>
        )
    }
}

const stateToProps = (state) => {
    return {
        announcementMode: state.announcements.mode,
        announcementError: state.announcements.error,
        hasPermissions: state.announcements.settings.can_create_messages,
        isAnnouncementsLimitReached: state.announcements.settings.is_limit_reached,
    }
}

export const dispatchToProps = (dispatch) => ({
    sendEnterAnnouncementsSoundState: () => dispatch(sendEnterAnnouncementsSoundState()),
    sendEnterLandingSoundState: () => dispatch(sendEnterLandingSoundState()),
    playEnterAnnouncementSound: () => dispatch(playEnterAnnouncementSound()),
    playExitAnnouncementSound: () => dispatch(playExitAnnouncementSound()),
    changeAnnouncementMode: (mode) => dispatch(changeMode(mode)),
    archiveAnnouncement: (data) => dispatch(archiveAnnouncement(data)),
    deleteAnnouncement: (data) => dispatch(deleteAnnouncement(data)),
    changeOpenedAnnouncement: (announcementId) => dispatch(changeOpenedAnnouncement(announcementId)),
})

export const AnnouncementOverlay = connect(stateToProps, dispatchToProps)(AnnouncementOverlayComponent)



// WEBPACK FOOTER //
// ./containers/ClanLanding/components/AnnouncementsCard/components/AnnouncementOverlay/AnnouncementOverlay.jsx